import LinkOff from '@mui/icons-material/LinkOff';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { type ReactElement, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import type { SubscriptionId } from '../../api';
import { ConfirmSheet } from '../../components/ConfirmSheet';
import { reasonMessage } from '../../i18n';
import type { ApiError } from '../../lib/api/api-client';

import { AFTER_STOP_PATH, stoppedCopyingState } from './stop-copying';
import { useStopCopying } from './use-stop-copying';

/** Props of {@link StopCopyingControl}. */
export interface StopCopyingControlProps {
  readonly subscriptionId: SubscriptionId;
  /** From `GET /me`; `null` when E7 does not know it (Q108): E12 then says "This channel". */
  readonly channelName: string | null;
}

function StopFailure({ error }: { readonly error: ApiError }): ReactElement {
  const { t } = useTranslation();
  // I12: the catalogue text; a network failure carries INTERNAL_ERROR. Alert is role="alert".
  const message = reasonMessage(error.reasonCode, error.params);
  return <Alert severity="error">{t(message.key, message.values)}</Alert>;
}

/**
 * "Stop copying this channel" on E7 (S9 R8, R9, R16; Proposal, to be validated, Q121): the canvas
 * E12 footer sends the trader here. C3's `ConfirmSheet` says, before anything is sent, that the
 * next signals stop and open positions stay tracked (FXM-01 §4). On success E7 is replaced by
 * E12, which says so; a failure stays in the open sheet (S8 R21 pattern). Shown for every status.
 */
export function StopCopyingControl({
  subscriptionId,
  channelName,
}: StopCopyingControlProps): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const onStopped = useCallback(() => {
    void navigate(AFTER_STOP_PATH, { replace: true, state: stoppedCopyingState(channelName) });
  }, [navigate, channelName]);
  const { state, run, reset } = useStopCopying(subscriptionId, onStopped);
  const busy = state.status === 'busy';
  return (
    <>
      <Button
        variant="text"
        color="error"
        onClick={() => {
          reset();
          setOpen(true);
        }}
        sx={{ minHeight: 44, fontWeight: 600, fontSize: 14 }}
      >
        {t('channels.stopCopying.button')}
      </Button>
      <ConfirmSheet
        open={open}
        title={t('channels.stopCopying.title')}
        confirmLabel={busy ? t('channels.stopCopying.stopping') : t('channels.stopCopying.confirm')}
        cancelLabel={t('channels.stopCopying.cancel')}
        icon={<LinkOff />}
        busy={busy}
        footer={state.status === 'failed' ? <StopFailure error={state.error} /> : undefined}
        onConfirm={() => {
          run();
        }}
        onCancel={() => {
          setOpen(false);
        }}
      >
        <Stack spacing={1}>
          <Typography sx={{ fontWeight: 600 }}>{t('channels.stopCopying.nextSignals')}</Typography>
          <Typography>{t('channels.stopCopying.positions')}</Typography>
        </Stack>
      </ConfirmSheet>
    </>
  );
}
